import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getNarrativeQuestion,
  saveNarrativeAnswer,
} from '../../services/apiService';
import { useUserStatus } from '../../context/UserStatusContext';
import styles from './WidgetStyles.module.css';

const DailyReflectionWidget = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { refreshUserStatus } = useUserStatus();
  const [question, setQuestion] = useState('Loading question...');
  const [answer, setAnswer] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(false);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);

  const fetchQuestion = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const data = await getNarrativeQuestion(accessToken);
      setQuestion(data.question);

      if (data.question.includes("You've answered all")) {
        setAllQuestionsAnswered(true);
      } else {
        setAllQuestionsAnswered(false);
      }
    } catch (error) {
      console.error('Failed to fetch narrative question:', error);
      setQuestion('Could not load a new question. Please try again later.');
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  const handleSave = async () => {
    if (!answer.trim()) return;
    setIsSaving(true);
    setLastSaved(false);
    try {
      const accessToken = await getAccessTokenSilently();
      await saveNarrativeAnswer({ question, answer }, accessToken);
      setAnswer('');
      await refreshUserStatus();
      setLastSaved(true);
      setTimeout(() => setLastSaved(false), 2000);
      await fetchQuestion();
    } catch (error) {
      console.error('Failed to save answer:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`${styles.widgetCard} ${styles.reflectionWidget} ${allQuestionsAnswered ? styles.completed : ''}`}
    >
      <h4 className={styles.panelTitle}>Daily Reflection</h4>
      <p className={styles.description}>
        Add a memory or thought to make your persona more authentic.
      </p>

      <div className={styles.questionBox}>
        <p className={styles.questionText}>{question}</p>

        {!allQuestionsAnswered && (
          <>
            <textarea
              className={styles.answerTextarea}
              rows="3"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Your thoughts..."
              disabled={isSaving}
            />
            <div className={styles.widgetFooter}>
              <button
                onClick={fetchQuestion}
                className="action-button secondary"
                disabled={isSaving}
              >
                Skip
              </button>
              <button
                onClick={handleSave}
                className="action-button primary"
                disabled={isSaving || !answer.trim()}
              >
                {isSaving
                  ? 'Saving...'
                  : lastSaved
                    ? 'Saved! ✔'
                    : 'Save & Next'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyReflectionWidget;
